import { logComponentError } from '@ms/yammer-diagnostics-state';
import type { ErrorCode } from '@ms/yammer-telemetry';
import { reportError } from '@ms/yammer-telemetry';
import { pick, pickBy } from 'lodash';

import { experimentConfigs } from '../../state/ecsExperiments/types';
import { featureRolloutNames } from '../../state/ecsFeatureRollouts/types';
import type {
  EcsConfig,
  EcsProjectTeamName,
  Experiment,
  ExperimentConfig,
  ExperimentTreatmentValue,
  ExperimentTreatments,
  FeatureRolloutTreatments,
} from '../../state/types';

export interface EcsPayload {
  readonly Headers: {
    readonly ETag: string;
  };
  readonly ConfigIDs: Readonly<Record<string, string>>;
  readonly YammerClients: Partial<FeatureRolloutTreatments & ExperimentTreatments> | undefined;
  readonly CrossPlatform: Partial<FeatureRolloutTreatments & ExperimentTreatments> | undefined;
  readonly ClientForward: Partial<FeatureRolloutTreatments & ExperimentTreatments> | undefined;
}

export interface NormalizedEcsEntities {
  readonly ecsConfig: EcsConfig;
  readonly ecsExperimentTreatments: Partial<ExperimentTreatments>;
  readonly ecsFeatureRolloutTreatments: Partial<FeatureRolloutTreatments>;
}

type SectionTreatments = Partial<FeatureRolloutTreatments & ExperimentTreatments>;

const experimentsComponentName = 'Experiments';

const experimentConfigsByName: Partial<Record<string, ExperimentConfig<readonly ExperimentTreatmentValue[]>>> =
  experimentConfigs;

type ReportExperimentValidationError = (error: Error, errorCode: ErrorCode) => void;
const reportExperimentValidationError: ReportExperimentValidationError = (error, errorCode) => {
  logComponentError(experimentsComponentName, error);
  reportError({ error, eventProperties: { errorCode } });
};

type IsValidExperimentTreatment = (
  experiment: string,
  treatmentValue: ExperimentTreatmentValue,
  projectTeam: EcsProjectTeamName
) => boolean;
const isValidExperimentTreatment: IsValidExperimentTreatment = (experiment, treatmentValue, projectTeam) => {
  const config = experimentConfigsByName[experiment];

  if (!config) {
    return false;
  }

  let isValid = true;

  if (config.projectTeam !== projectTeam) {
    reportExperimentValidationError(
      new Error(
        `Experiments validation failed - experiment "${experiment}" was received under projectTeam "${projectTeam}" but is configured for projectTeam "${config.projectTeam}"`
      ),
      'EcsExperimentProjectTeamMismatch'
    );
    isValid = false;
  }

  if (!config.treatments.some((validTreatment) => validTreatment === treatmentValue)) {
    reportExperimentValidationError(
      new Error(
        `Experiments validation failed - experiment "${experiment}" received treatment "${treatmentValue}" which is not one of its configured treatments: ${config.treatments.join(
          ', '
        )}`
      ),
      'EcsExperimentInvalidTreatment'
    );
    isValid = false;
  }

  return isValid;
};

type PickValidatedExperimentTreatments = (
  experimentTreatments: SectionTreatments | undefined,
  projectTeam: EcsProjectTeamName
) => Partial<ExperimentTreatments>;
const pickValidatedExperimentTreatments: PickValidatedExperimentTreatments = (experimentTreatments, projectTeam) => {
  if (!experimentTreatments) {
    return {};
  }

  return pickBy(experimentTreatments, (treatmentValue, experiment: Experiment) =>
    isValidExperimentTreatment(experiment, treatmentValue, projectTeam)
  );
};

type NormalizeEcsPayload = (payload: EcsPayload) => NormalizedEcsEntities;
export const normalizeEcsPayload: NormalizeEcsPayload = ({
  ConfigIDs: configIds,
  Headers: { ETag: etag },
  YammerClients: yammerClientsTreatments,
  CrossPlatform: crossPlatformTreatments,
  ClientForward: clientForwardTreatments,
}) => ({
  ecsConfig: { configIds, etag },
  ecsExperimentTreatments: {
    ...pickValidatedExperimentTreatments(yammerClientsTreatments, 'YammerClients'),
    ...pickValidatedExperimentTreatments(crossPlatformTreatments, 'CrossPlatform'),
    ...pickValidatedExperimentTreatments(clientForwardTreatments, 'ClientForward'),
  },
  ecsFeatureRolloutTreatments: yammerClientsTreatments ? pick(yammerClientsTreatments, featureRolloutNames) : {},
});
